import { Fragment, type ReactNode } from 'react';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import { formatCurrency } from '@voll-intelligence/intelligence-ui-kit/checkout';
import type {
  FlightSeatMapColumn,
  FlightSeatMapRow,
  FlightSeatMapSeat,
  FlightSeatMapView,
} from './flight_seat_map_port.js';
import './flight_seat_map.css';

const STATE_LEGEND_KEY = {
  free: 'flightSeatMap.legendFree',
  paid: 'flightSeatMap.legendPaid',
  extra: 'flightSeatMap.legendExtra',
  occupied: 'flightSeatMap.legendOccupied',
  unavailable: 'flightSeatMap.legendUnavailable',
} as const;

/** Estados exibidos na legenda — `unavailable` não tem chave/cor própria no
 *  handoff visual, só aparece (cinza igual a `occupied`) nas células. */
const LEGEND_STATES = ['free', 'paid', 'extra', 'occupied'] as const;

type TemplateCell = { key: string } & (
  | { type: 'num' }
  | { type: 'gap' }
  | { type: 'seat'; column: FlightSeatMapColumn }
);

/** Uma célula por coluna + uma célula de corredor (gap) após cada
 *  `aisleAfter`, com o número da fileira nas DUAS pontas (primeira e última
 *  célula) — anatomia canônica de fuselagem. Fuselagens sem corredor marcado
 *  (menos de 3 colunas, ex. turboélice 1+1) já ficam simétricas por
 *  construção: um `num` de cada lado, sem gap no meio. Cabeçalho e fileiras
 *  mapeiam o MESMO array, então não podem divergir. A `key` nomeia a célula
 *  pela coluna que ela é (ou segue), não pela posição; o fornecedor não
 *  deduplica as colunas declaradas, então a `key` leva a ocorrência da letra
 *  como prefixo numérico separado — nenhuma letra literal colide com ele. */
function buildTemplate(columns: readonly FlightSeatMapColumn[]): TemplateCell[] {
  const cells: TemplateCell[] = [{ key: 'num-start', type: 'num' }];
  const seen = new Map<string, number>();
  for (const column of columns) {
    const occurrence = (seen.get(column.letter) ?? 0) + 1;
    seen.set(column.letter, occurrence);
    const id = `${String(occurrence)}-${column.letter}`;
    cells.push({ key: `seat-${id}`, type: 'seat', column });
    if (column.aisleAfter) cells.push({ key: `gap-${id}`, type: 'gap' });
  }
  cells.push({ key: 'num-end', type: 'num' });
  return cells;
}

/** Fileiras de saída CONSECUTIVAS (par comum em A320, LATAM incluída)
 *  compartilham uma única banda — repetir "SAÍDA DE EMERGÊNCIA" fileira sim,
 *  fileira sim é ruído visual, e o par sempre se refere ao mesmo par de
 *  portas. A banda marca só a PRIMEIRA fileira de cada bloco contíguo; o
 *  `aria-label` de cada fileira (grupo) continua individual — a colagem é
 *  puramente visual. */
function exitBandRows(rows: readonly FlightSeatMapRow[]): ReadonlySet<FlightSeatMapRow> {
  const set = new Set<FlightSeatMapRow>();
  let previousExit = false;
  for (const row of rows) {
    if (row.exit && !previousExit) set.add(row);
    previousExit = row.exit;
  }
  return set;
}

/** Fileiras sobre a asa contíguas a partir da PRIMEIRA `overWing`: um único
 *  bloco ganha as bandas da asa. Ocorrências não-contíguas depois desse bloco
 *  ficam de fora (caem em `post`, renderizadas sem banda) — regra simples e
 *  seguras o bastante para o formato real dos mapas do Sabre. */
function splitWingRange(rows: readonly FlightSeatMapRow[]): {
  pre: FlightSeatMapRow[];
  wing: FlightSeatMapRow[];
  post: FlightSeatMapRow[];
} {
  const first = rows.findIndex((row) => row.overWing);
  if (first === -1) return { pre: [...rows], wing: [], post: [] };
  let last = first;
  while (last + 1 < rows.length && rows[last + 1]?.overWing) last++;
  return {
    pre: rows.slice(0, first),
    wing: rows.slice(first, last + 1),
    post: rows.slice(last + 1),
  };
}

/** Grade INFORMATIVA (sem seleção): células são <span> com aria-label, não
 *  botões — não há ação possível pré-PNR. */
export function FlightSeatMapGrid({ view }: { readonly view: FlightSeatMapView }): ReactNode {
  const { t, locale } = useGenUiI18n();
  const fmt = (p: { amount: string; currency: string }) =>
    formatCurrency(Number(p.amount), p.currency, locale);

  const summaryParts = [
    view.summary.availableSeats === 1
      ? t('flightSeatMap.available_one')
      : t('flightSeatMap.available_other', { count: view.summary.availableSeats }),
    view.summary.freeFromRow != null
      ? t('flightSeatMap.freeFromRow', { row: view.summary.freeFromRow })
      : undefined,
    view.summary.minPaid
      ? t('flightSeatMap.paidFrom', { price: fmt(view.summary.minPaid) })
      : undefined,
  ].filter((part): part is string => Boolean(part));

  /** Composição única do rótulo do assento — usada tanto no `aria-label`
   *  (leitor de tela) quanto no tooltip visual (`.fsm-seat-tip`), para que os
   *  dois textos nunca possam divergir. */
  function seatLabel(rowNumber: number, seat: FlightSeatMapSeat): string {
    const price = seat.price ? ` · ${fmt(seat.price)}` : '';
    return `${String(rowNumber)}${seat.column} · ${t(STATE_LEGEND_KEY[seat.state])}${price}`;
  }

  const template = buildTemplate(view.columns);
  const gridTemplateColumns = template
    .map((cell) => {
      if (cell.type === 'num') return '28px';
      if (cell.type === 'gap') return '18px';
      return 'var(--fsm-seat)';
    })
    .join(' ');

  function renderRow(
    row: FlightSeatMapRow,
    showExitBand: ReadonlySet<FlightSeatMapRow>,
  ): ReactNode {
    const seatByColumn = new Map(row.seats.map((seat) => [seat.column, seat]));
    const rowClassName = [
      'fsm-row',
      row.exit ? 'fsm-row--exit' : undefined,
      row.overWing ? 'fsm-row--wing' : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Fragment key={row.number}>
        {showExitBand.has(row) && (
          <div className="fsm-exit-band" aria-hidden="true">
            {t('flightSeatMap.exitRow')}
          </div>
        )}
        <div
          className={rowClassName}
          style={{ gridTemplateColumns }}
          {...(row.exit
            ? {
                role: 'group',
                'aria-label': `${t('flightSeatMap.rowAria', { row: row.number })} · ${t('flightSeatMap.exitRow')}`,
              }
            : {})}
        >
          {template.map((cell) => {
            if (cell.type === 'num') {
              return (
                <span key={cell.key} className="fsm-rownum">
                  {row.number}
                </span>
              );
            }
            if (cell.type === 'gap') return <span key={cell.key} />;
            const seat = seatByColumn.get(cell.column.letter);
            if (!seat) return <span key={cell.key} className="fsm-seat" />;
            const label = seatLabel(row.number, seat);
            return (
              <span
                key={cell.key}
                className={`fsm-seat fsm-seat--${seat.state}`}
                role="img"
                aria-label={label}
              >
                <span className="fsm-seat-back" aria-hidden="true">
                  {seat.column}
                </span>
                <span className="fsm-seat-cushion" aria-hidden="true" />
                <span className="fsm-seat-tip" role="presentation">
                  {label}
                </span>
              </span>
            );
          })}
        </div>
      </Fragment>
    );
  }

  const { pre, wing, post } = splitWingRange(view.rows);
  const showExitBand = exitBandRows(view.rows);

  return (
    <div className="fsm">
      <p className="fsm-summary">
        {summaryParts.map((part) => (
          <Fragment key={part}>
            {part !== summaryParts[0] && <span aria-hidden="true"> · </span>}
            <span>{part}</span>
          </Fragment>
        ))}
      </p>
      <div className="fsm-legend">
        {LEGEND_STATES.map((state) => (
          <span key={state}>
            <i className={`fsm-key fsm-seat--${state}`} aria-hidden="true">
              <span className="fsm-seat-back" />
              <span className="fsm-seat-cushion" />
            </i>
            {t(STATE_LEGEND_KEY[state])}
          </span>
        ))}
      </div>
      <div className="fsm-grid">
        <div className="fsm-fuselage">
          <div className="fsm-colhead" style={{ gridTemplateColumns }}>
            {template.map((cell) => (
              <span key={cell.key}>{cell.type === 'seat' ? cell.column.letter : ''}</span>
            ))}
          </div>
          {pre.map((row) => renderRow(row, showExitBand))}
          {wing.length > 0 && (
            <div className="fsm-wing-range">
              <span className="fsm-wing fsm-wing--left" aria-hidden="true" />
              <span className="fsm-wing fsm-wing--right" aria-hidden="true" />
              {wing.map((row) => renderRow(row, showExitBand))}
            </div>
          )}
          {post.map((row) => renderRow(row, showExitBand))}
        </div>
      </div>
      <p className="fsm-disclaimer">{t('flightSeatMap.disclaimer')}</p>
    </div>
  );
}
